import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";

const CANVAS_W = 1200;
const CANVAS_H = 800;

export default function FreeCanvasRender({ page }) {
  const [elements, setElements] = useState([]);
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    api.get(`/free-canvas/${page}`).then((r) => setElements(r.data.elements || [])).catch(() => {});
  }, [page]);

  useEffect(() => {
    if (!wrapRef.current) return;
    const update = () => setScale(wrapRef.current.clientWidth / CANVAS_W);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(wrapRef.current);
    return () => observer.disconnect();
  }, [elements.length]);

  if (!elements.length) return null;

  return (
    <section className="px-5 pb-16 md:px-10" data-testid={`free-canvas-${page}`}>
      <div
        ref={wrapRef}
        className="relative mx-auto max-w-6xl overflow-hidden"
        style={{ height: CANVAS_H * scale }}
      >
        <div style={{ width: CANVAS_W, height: CANVAS_H, transform: `scale(${scale})`, transformOrigin: "top left" }}>
          {[...elements]
            .sort((a, b) => (a.z || 0) - (b.z || 0))
            .map((el) => {
              const p = el.props || {};
              const style = {
                position: "absolute",
                left: el.x,
                top: el.y,
                width: el.w,
                height: el.h,
                zIndex: el.z || 1,
                opacity: p.opacity ?? 1,
              };
              return (
                <div key={el.id} style={style} data-testid={`free-el-${el.id}`}>
                  {el.type === "text" && (
                    <div
                      className="flex h-full w-full items-center overflow-hidden"
                      style={{ justifyContent: p.align === "center" ? "center" : p.align === "right" ? "flex-end" : "flex-start" }}
                    >
                      <span style={{ fontSize: p.fontSize, color: p.color, fontWeight: p.bold ? 700 : 400, textAlign: p.align, lineHeight: 1.25 }}>
                        {p.text}
                      </span>
                    </div>
                  )}
                  {el.type === "image" && p.url && (
                    <img src={p.url} alt="" className="h-full w-full object-cover" style={{ borderRadius: p.radius || 0 }} loading="lazy" />
                  )}
                  {el.type === "button" && (
                    <Link
                      to={p.href || "/"}
                      className="flex h-full w-full items-center justify-center text-[13px] font-semibold uppercase tracking-wider transition-opacity hover:opacity-85"
                      style={{ background: p.bg, color: p.color, borderRadius: p.radius ?? 999 }}
                      data-testid={`free-btn-${el.id}`}
                    >
                      {p.label}
                    </Link>
                  )}
                  {el.type === "block" && (
                    <div className="h-full w-full" style={{ background: p.bg, borderRadius: p.radius || 0, border: `1px solid ${p.border || "transparent"}` }} />
                  )}
                </div>
              );
            })}
        </div>
      </div>
    </section>
  );
}
